/**
 * El chat de ayuda del vendedor. **Explica; no ejecuta.**
 *
 * Que es
 * ------
 * Una ventanita que se abre desde cualquier pantalla del ERP. El vendedor
 * escribe en criollo —«donde anoto lo que vendi», «no me deja recalcular»— y
 * recibe el titulo del proceso, donde se hace, los pasos numerados con los
 * nombres exactos de los botones, las trampas conocidas y **un enlace que lo
 * lleva a la pantalla**. Eso ultimo es lo que lo hace util de verdad: no le
 * decis donde esta, lo llevas.
 *
 * La frontera, y no se negocia
 * ----------------------------
 * El chat SOLO EXPLICA. No carga, no edita, no borra, no emite, no factura, no
 * ejecuta ninguna accion de servidor, ni siquiera con confirmacion. En este
 * archivo eso se ve en tres cosas concretas:
 *
 * 1. **No hay un solo `useMutation` ni un `POST`.** Lo unico que se importa de
 *    la red es `components/guia/api`, que solo sabe hacer GET.
 * 2. **Lo unico que este componente hace ademas de dibujar es `navigate()`**, y
 *    solo cuando la persona toca el enlace. Llevarla a una pantalla no es
 *    ejecutar nada: lo que haga ahi lo hace ella.
 * 3. **El renglon fijo esta siempre a la vista** —«Te digo como se hace; los
 *    pasos los das vos»—, y el texto viene del servidor para que diga lo mismo
 *    en la pantalla y en cualquier otro cliente.
 *
 * Por que un boton flotante y no uno en la barra de arriba
 * --------------------------------------------------------
 * Se probaron las dos y gano el flotante, por tres motivos:
 *
 * * **La barra de arriba ya esta llena y es la que peor aguanta otro control.**
 *   Tiene migas, buscador, selector de empresa y tema, y por debajo de `sm` se
 *   parte en dos filas justamente porque no entra. Un sexto boton ahi empuja al
 *   selector de empresa, que es el dato mas critico de esta pantalla —saber en
 *   que consorcio se esta parado— y que ya tiene su renglon propio en movil.
 * * **Un boton flotante se ve sin buscarlo.** El destinatario es alguien que
 *   recien empieza y no sabe que existe la ayuda; un icono mas en una fila de
 *   iconos chicos es exactamente lo que no encuentra. Abajo a la derecha es
 *   ademas donde la gente ya busca la ayuda en cualquier sistema.
 * * **Es la modificacion mas chica al codigo de otro.** Hay otro equipo tocando
 *   el sistema ahora mismo: esto entra con dos renglones en `AppShell` y sin
 *   tocar `TopBar`, que es un archivo que ellos pueden estar editando.
 *
 * El costo es que tapa una esquina. Se paga con `bottom-4 right-4`, tamano
 * chico, y `z-40`: por debajo de la paleta de comandos y de los avisos
 * (`z-50`), que son mas urgentes que la ayuda.
 *
 * Accesibilidad
 * -------------
 * El panel es un `dialog` **no modal** a proposito: la idea es leer los pasos
 * *mientras* se mira la pantalla de la que hablan, asi que no se bloquea el
 * fondo ni se atrapa el foco. Escape cierra y devuelve el foco al boton; al
 * abrir, el foco va al campo de texto; cada respuesta se anuncia por
 * `aria-live`.
 */

import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  buscarEnLaGuia,
  esInicioValido,
  leerProcesoDeLaGuia,
  useInicioGuia,
  type ProcesoCompleto,
  type ProcesoResumen,
  type RespuestaBusqueda,
} from '@/components/guia/api';
import {
  IconAlert,
  IconArrowRight,
  IconChat,
  IconClose,
  IconLifeBuoy,
  IconSearch,
  IconSpinner,
} from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';

/** Un turno de la conversacion. La pregunta y lo que vino. */
interface Turno {
  id: number;
  pregunta: string;
  /** `null` mientras se espera al servidor. */
  respuesta: RespuestaBusqueda | null;
  /** Texto del error, si la consulta no llego a destino. */
  error: string | null;
}

let proximoTurno = 1;

export function ChatGuia() {
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState('');
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [buscando, setBuscando] = useState(false);

  const navigate = useNavigate();
  const { data: inicio } = useInicioGuia();

  const disparador = useRef<HTMLButtonElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const hilo = useRef<HTMLDivElement>(null);

  // Escape cierra desde donde sea, incluso con el foco en el campo.
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        setAbierto(false);
        disparador.current?.focus();
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  // Al abrir, el foco al campo: quien lo abrio ya sabe que quiere preguntar.
  useEffect(() => {
    if (abierto) campo.current?.focus();
  }, [abierto]);

  // Cada respuesta nueva baja el hilo. Sin esto, la respuesta larga aparece
  // arriba de todo y parece que no pasó nada.
  //
  // Se asigna `scrollTop` en vez de llamar a `scrollTo({behavior:'smooth'})`, y
  // no es por gusto: el scroll suave tarda, así que la respuesta recién llegada
  // pasa un rato fuera de vista —se ve en las pruebas— y encima `scrollTo` no
  // existe en jsdom, o sea que la versión linda no se puede ni testear. Saltar
  // al final es además lo que hace cualquier chat.
  useEffect(() => {
    const nodo = hilo.current;
    if (nodo === null) return;
    nodo.scrollTop = nodo.scrollHeight;
  }, [turnos]);

  const preguntar = useCallback(
    async (texto: string) => {
      const pregunta = texto.trim();
      if (pregunta === '' || buscando) return;

      const id = proximoTurno++;
      setTurnos((previos) => [...previos, { id, pregunta, respuesta: null, error: null }]);
      setConsulta('');
      setBuscando(true);
      try {
        const respuesta = await buscarEnLaGuia(pregunta);
        setTurnos((previos) =>
          previos.map((turno) => (turno.id === id ? { ...turno, respuesta } : turno)),
        );
      } catch (error) {
        // El chat no puede inventar una respuesta cuando el servidor no
        // contesta: se dice que no se pudo consultar y se deja el texto para
        // reintentar.
        const mensaje =
          error instanceof Error ? error.message : 'No se pudo consultar la guía.';
        setTurnos((previos) =>
          previos.map((turno) => (turno.id === id ? { ...turno, error: mensaje } : turno)),
        );
      } finally {
        setBuscando(false);
      }
    },
    [buscando],
  );

  /** Abre un proceso concreto: es lo que hace un «quizás buscabas…». */
  const abrirProceso = useCallback(async (resumen: ProcesoResumen) => {
    const id = proximoTurno++;
    setTurnos((previos) => [
      ...previos,
      { id, pregunta: resumen.pregunta_principal, respuesta: null, error: null },
    ]);
    setBuscando(true);
    try {
      const datos = await leerProcesoDeLaGuia(resumen.id);
      setTurnos((previos) =>
        previos.map((turno) =>
          turno.id === id
            ? {
                ...turno,
                respuesta: {
                  consulta: resumen.pregunta_principal,
                  estado: 'encontrado',
                  respuesta: datos.respuesta,
                  alternativas: datos.alternativas,
                  mensaje: null,
                  entendi: {},
                  aclaracion: datos.aclaracion,
                },
              }
            : turno,
        ),
      );
    } catch (error) {
      const mensaje = error instanceof Error ? error.message : 'No se pudo abrir ese proceso.';
      setTurnos((previos) =>
        previos.map((turno) => (turno.id === id ? { ...turno, error: mensaje } : turno)),
      );
    } finally {
      setBuscando(false);
    }
  }, []);

  const irA = useCallback(
    (ruta: string) => {
      setAbierto(false);
      navigate(ruta);
    },
    [navigate],
  );

  // Sin guía servida, no hay chat. Ver `esInicioValido`: el archivo suelto de
  // demostración contesta 200 y vacío a las rutas que no conoce, así que un
  // botón que confiara en "no hubo error" ahí adentro se abriría para no
  // contestar nada.
  if (!esInicioValido(inicio)) return null;

  return (
    <>
      {!abierto && (
        <button
          ref={disparador}
          type="button"
          onClick={() => {
            setAbierto(true);
          }}
          className="fixed bottom-4 right-4 z-40 flex h-12 items-center gap-2 rounded-full bg-accent px-4 text-sm font-medium text-accent-contrast shadow-lg transition-transform hover:scale-[1.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:bottom-6 sm:right-6"
          aria-label="Abrir la ayuda: preguntá cómo se hace algo"
        >
          <IconLifeBuoy className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">¿Cómo se hace?</span>
        </button>
      )}

      {abierto && (
        <div
          role="dialog"
          aria-label="Ayuda: cómo se hace"
          className="fixed inset-x-0 bottom-0 z-40 flex max-h-[85svh] flex-col rounded-t-xl border border-line bg-surface shadow-lg animate-slide-up sm:inset-x-auto sm:bottom-6 sm:right-6 sm:h-[min(38rem,85svh)] sm:w-[26rem] sm:rounded-xl"
        >
          <header className="flex items-start gap-3 border-b border-line px-4 py-3">
            <span
              aria-hidden="true"
              className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent"
            >
              <IconChat className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold">¿Cómo se hace?</h2>
              <p className="truncate text-xs text-fg-muted">
                {inicio.procesos} procesos del sistema, escritos y verificados.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                disparador.current?.focus();
              }}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-fg-muted transition-colors hover:bg-fg/5 hover:text-fg"
              aria-label="Cerrar la ayuda"
            >
              <IconClose className="h-4 w-4" />
            </button>
          </header>

          <div
            ref={hilo}
            className="flex-1 space-y-4 overflow-y-auto px-4 py-4"
            aria-live="polite"
            aria-busy={buscando}
          >
            {turnos.length === 0 && (
              <Bienvenida
                sugeridas={inicio.sugeridas}
                onElegir={(pregunta) => {
                  void preguntar(pregunta);
                }}
              />
            )}

            {turnos.map((turno) => (
              <TurnoDeChat
                key={turno.id}
                turno={turno}
                onIrA={irA}
                onAbrirProceso={(resumen) => {
                  void abrirProceso(resumen);
                }}
              />
            ))}
          </div>

          <form
            className="border-t border-line px-4 py-3"
            onSubmit={(evento) => {
              evento.preventDefault();
              void preguntar(consulta);
            }}
          >
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="guia-consulta">
                Escribí tu pregunta
              </label>
              <div className="relative flex-1">
                <IconSearch
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint"
                  aria-hidden="true"
                />
                <input
                  ref={campo}
                  id="guia-consulta"
                  type="text"
                  value={consulta}
                  onChange={(evento) => {
                    setConsulta(evento.target.value);
                  }}
                  placeholder="¿Cómo cargo una venta?"
                  autoComplete="off"
                  maxLength={300}
                  className="h-10 w-full rounded-md border border-line bg-app pl-8 pr-3 text-sm text-fg placeholder:text-fg-faint focus:border-accent focus:outline-none"
                />
              </div>
              <Button type="submit" variant="primary" loading={buscando} disabled={consulta.trim() === ''}>
                Preguntar
              </Button>
            </div>
            {/* El renglón fijo. Viene del servidor: es la promesa del producto,
                no una decoración del frontend. */}
            <p className="mt-2 text-2xs uppercase tracking-wide text-fg-faint">
              {inicio.aclaracion}
            </p>
          </form>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Piezas
// ---------------------------------------------------------------------------
function Bienvenida({
  sugeridas,
  onElegir,
}: {
  sugeridas: { id: string; pregunta: string }[];
  onElegir: (pregunta: string) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">
        Preguntame cómo se hace algo del sistema y te digo dónde está y los pasos, con el nombre
        exacto de cada botón. Si no lo sé, te lo digo.
      </p>
      <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">
        Por ejemplo
      </p>
      <ul className="space-y-1.5">
        {sugeridas.map((sugerida) => (
          <li key={sugerida.id}>
            <button
              type="button"
              onClick={() => {
                onElegir(sugerida.pregunta);
              }}
              className="w-full rounded-md border border-line bg-app px-3 py-2 text-left text-sm text-fg transition-colors hover:border-accent hover:bg-accent-soft"
            >
              {sugerida.pregunta}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TurnoDeChat({
  turno,
  onIrA,
  onAbrirProceso,
}: {
  turno: Turno;
  onIrA: (ruta: string) => void;
  onAbrirProceso: (resumen: ProcesoResumen) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="ml-auto w-fit max-w-[85%] rounded-lg rounded-br-sm bg-accent px-3 py-2 text-sm text-accent-contrast">
        {turno.pregunta}
      </p>

      {turno.respuesta === null && turno.error === null && (
        <p className="flex items-center gap-2 text-sm text-fg-muted">
          <IconSpinner className="h-4 w-4" aria-hidden="true" />
          Buscando en la guía…
        </p>
      )}

      {turno.error !== null && (
        <p className="rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-sm text-fg">
          No pude consultar la guía: {turno.error}
        </p>
      )}

      {turno.respuesta !== null && <Respuesta datos={turno.respuesta} onIrA={onIrA} onAbrirProceso={onAbrirProceso} />}
    </div>
  );
}

function Respuesta({
  datos,
  onIrA,
  onAbrirProceso,
}: {
  datos: RespuestaBusqueda;
  onIrA: (ruta: string) => void;
  onAbrirProceso: (resumen: ProcesoResumen) => void;
}) {
  const proceso = datos.respuesta;

  return (
    <div className="space-y-3">
      {/* Cuando no encontró, lo dice. Nunca se dibuja el primer resultado como
          si fuera la respuesta. */}
      {proceso === null && datos.mensaje !== null && (
        <p className="text-sm text-fg">{datos.mensaje}</p>
      )}

      {proceso !== null && <FichaDelProceso proceso={proceso} onIrA={onIrA} />}

      {datos.alternativas.length > 0 && (
        <div className="space-y-1.5">
          {proceso !== null && (
            <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">
              Quizás buscabas
            </p>
          )}
          {datos.alternativas.map((alternativa) => (
            <button
              key={alternativa.id}
              type="button"
              onClick={() => {
                onAbrirProceso(alternativa);
              }}
              className="flex w-full items-center gap-2 rounded-md border border-line bg-app px-3 py-2 text-left text-sm text-fg transition-colors hover:border-accent hover:bg-accent-soft"
            >
              <span className="min-w-0 flex-1 truncate">{alternativa.titulo}</span>
              <IconArrowRight className="h-3.5 w-3.5 shrink-0 text-fg-faint" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FichaDelProceso({
  proceso,
  onIrA,
}: {
  proceso: ProcesoCompleto;
  onIrA: (ruta: string) => void;
}) {
  return (
    <article className="space-y-3 rounded-lg border border-line bg-app px-3 py-3">
      <h3 className="text-sm font-semibold">{proceso.titulo}</h3>

      {/* El aviso de lo no verificado va ARRIBA de los pasos, no al pie: si va
          abajo se lee después de haber hecho lo que dice. */}
      {proceso.verificado === false && proceso.aviso !== null && (
        <p className="flex gap-2 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-2 text-xs text-fg">
          <IconAlert className="mt-px h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
          <span>{proceso.aviso}</span>
        </p>
      )}

      <div>
        <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">Dónde se hace</p>
        <p className="mt-0.5 text-sm text-fg-muted">{proceso.donde_se_hace}</p>
      </div>

      {/* El enlace. Es lo que convierte una instrucción en un clic. Sólo está si
          el backend lo pudo resolver contra el árbol de menús de esta persona:
          nunca se arma una ruta acá. */}
      {proceso.enlace !== null && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            onIrA(proceso.enlace?.ruta ?? '/');
          }}
        >
          Ir a {proceso.enlace.pantalla}
          <IconArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}

      {proceso.pasos.length > 0 && (
        <div>
          <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">Los pasos</p>
          <ol className="mt-1 space-y-1.5">
            {proceso.pasos.map((paso, indice) => (
              <li key={paso} className="flex gap-2 text-sm text-fg">
                <span
                  aria-hidden="true"
                  className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-accent-soft text-2xs font-semibold text-accent"
                >
                  {indice + 1}
                </span>
                <span className="min-w-0">{paso}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {proceso.no_hace.length > 0 && (
        <div>
          <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">
            Esto NO hace
          </p>
          <ul className="mt-1 space-y-1">
            {proceso.no_hace.map((renglon) => (
              <li key={renglon} className="text-sm text-fg-muted">
                {renglon}
              </li>
            ))}
          </ul>
        </div>
      )}

      {proceso.trampas.length > 0 && (
        <div>
          <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">
            Ojo con esto
          </p>
          <ul className="mt-1 space-y-1">
            {proceso.trampas.map((trampa) => (
              <li key={trampa} className="flex gap-2 text-sm text-fg-muted">
                <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-warning" />
                <span className="min-w-0">{trampa}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {proceso.donde_se_ve.length > 0 && (
        <div>
          <p className="text-2xs font-medium uppercase tracking-wide text-fg-faint">
            Dónde se mira
          </p>
          <ul className="mt-1 space-y-1.5">
            {proceso.donde_se_ve.map((entrada) => (
              <li key={entrada.pregunta} className="text-sm">
                <span className="text-fg">{entrada.pregunta}</span>{' '}
                {entrada.enlace === null ? (
                  <span className="text-fg-muted">— {entrada.pantalla}</span>
                ) : (
                  <button
                    type="button"
                    className={clsx(
                      'text-accent underline underline-offset-2',
                      'hover:text-accent-hover',
                    )}
                    onClick={() => {
                      onIrA(entrada.enlace?.ruta ?? '/');
                    }}
                  >
                    {entrada.pantalla}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}
